import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Box, Card, CardContent, Grid } from "@mui/material";
import { useForm } from "react-hook-form";

import { endpointPrincipal } from "../../../../../api/endPoints/endpointPrincipal/endpointPrincipal";

import { CriacaoEdicaoUsuarioInterface } from "../../../../../types/UsuarioInterface";

import ModalLoading from "../../../../../components/Formularios/Modal/ModalLoading";
import FormTitulo from "../../../../../components/Formularios/Container/FormTitulo";
import FormActions from "../../../../../components/Formularios/Buttons/FormActions";
import CustomTextField from "../../../../../components/Formularios/Inputs/CustomTextField";
import SelectCodSistema from "../../../../../components/Formularios/Inputs/SelectCodSistema";
import SwitchAtivo from "../../../../../components/Formularios/Inputs/SwitchAtivo";
import { SelectEmpresas } from "./components/SelectEmpresas";
import { SelectFiliais } from "./components/SelectFiliais";

// Estendendo a interface para incluir os novos campos
interface FormUsuarioExtendido extends CriacaoEdicaoUsuarioInterface {
  funcao?: string;
  grupo?: string;
  id_filial?: number;
}

interface FormUsuarioProps {
  idUsuario?: string;
  acao?: string;
}

const FormUsuario = ({ idUsuario, acao }: FormUsuarioProps) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormUsuarioExtendido>({
    defaultValues: { situacao: 1, backoffice: 0, responsavelEmpresa: 0 },
  });

  const isAgente = watch("backoffice");
  const empresaSelecionada = watch("id_empresa");
  const navigate = useNavigate();

  // Define se está no modo de visualização
  const isVisualizando = acao === 'Visualizar';

  const isNovoUsuario = idUsuario === "novo";

  const [loading, setLoading] = useState<boolean>(false);

  const carregarUsuarioExistentePorId = async () => {
    setLoading(true);
    try {
      const { data } = await endpointPrincipal.get(`/usuarios/${idUsuario}`);

      // Extrair os valores de funcao e grupo do objeto agente, se existir
      const dadosFormatados = { ...data };

      if (data.agente) {
        dadosFormatados.funcao = data.agente.funcao;
        dadosFormatados.grupo = data.agente.grupo;
        // Remover o objeto agente para evitar que seja enviado na requisição PUT
        delete dadosFormatados.agente;
      }

      reset(dadosFormatados);
    } catch (error) {
      toast.error("Falha ao carregar dados. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNovoUsuario) {
      carregarUsuarioExistentePorId();
    }
  }, []);

  const atualizarUsuarioPorId = async (data: FormUsuarioExtendido) => {
    setLoading(true);
    try {
      // Criar uma cópia dos dados e remover o objeto agente, se existir
      const dadosParaEnvio = { ...data };
      if ('agente' in dadosParaEnvio) {
        delete dadosParaEnvio.agente;
      }

      await endpointPrincipal.put(`/usuarios/${idUsuario}`, dadosParaEnvio);
      toast.success(`Usuario editado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error("Falha ao alterar Usuario. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  const incluirNovoUsuario = async (data: FormUsuarioExtendido) => {
    setLoading(true);
    try {
      // Criar uma cópia dos dados e remover o objeto agente, se existir
      const dadosParaEnvio = { ...data };
      if ('agente' in dadosParaEnvio) {
        delete dadosParaEnvio.agente;
      }

      await endpointPrincipal.post("/usuarios", dadosParaEnvio);
      toast.success(`Usuario adicionado com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error(`Erro ao adicionar Usuario`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (usuario: FormUsuarioExtendido) => {
    if (isNovoUsuario) {
      incluirNovoUsuario(usuario);
    } else {
      atualizarUsuarioPorId(usuario);
    }
  };

  return (
    <>
      <Card>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <FormTitulo titulo="Usuários" acao={acao} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomTextField
                  label="Nome"
                  name="nome"
                  register={register}
                  rules={{ required: true }}
                  disabled={isVisualizando}
                />
              </Grid>

              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="Usuário"
                  name="nome_usuario"
                  register={register}
                  rules={{ required: true }}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="E-mail"
                  name="email"
                  register={register}
                  rules={{
                    required: true,
                    setValueAs: (valor: string) =>
                      typeof valor === "string" ? valor.trim() : valor,
                  }}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="Telefone"
                  name="telefone"
                  register={register}
                  rules={{ required: true }}
                  disabled={isVisualizando}
                />
              </Grid>

              {isNovoUsuario && (
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="Senha"
                    name="senha"
                    type="password"
                    register={register}
                    rules={{ required: true, minLength: 5 }}
                    error={!!errors.senha}
                    helperText={
                      errors.senha &&
                      "A senha é obrigatória e deve ter pelo menos 5 caracteres."
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12} lg={4}>
                <CustomTextField
                  label="CPF"
                  name="cpf"
                  register={register}
                  rules={{
                    setValueAs: (valor: string) =>
                      typeof valor === "string" ? valor.trim() : valor,
                    validate: (valor) => {
                      const digitos = (valor ?? "").trim().replace(/\D/g, "");
                      if (digitos.length === 0) return true;
                      return (
                        digitos.length === 11 ||
                        "Informe um CPF com 11 dígitos ou deixe o campo em branco."
                      );
                    },
                  }}
                  disabled={isVisualizando}
                  error={!!errors.cpf}
                  helperText={errors.cpf?.message}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <SelectEmpresas
                  name="id_empresa"
                  control={control}
                  label="Empresa"
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <SelectFiliais
                  name="id_filial"
                  control={control}
                  label="Filial (opcional)"
                  idEmpresa={empresaSelecionada}
                  disabled={isVisualizando}
                />
              </Grid>

              {isAgente === 1 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <SelectCodSistema
                      tabela="FUNCOES"
                      label="Função"
                      name="funcao"
                      control={control}
                      disabled={isVisualizando}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SelectCodSistema
                      tabela="GRUPOS"
                      label="Grupo"
                      name="grupo"
                      control={control}
                      disabled={isVisualizando}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={6} lg={3}>
                <SwitchAtivo name="situacao" control={control} disabled={isVisualizando} />
              </Grid>
              <Grid item xs={6} lg={3}>
                <SwitchAtivo
                  name="backoffice"
                  label="É Agente?"
                  control={control}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SwitchAtivo
                  name="responsavelEmpresa"
                  label="Ver dados da empresa?"
                  control={control}
                  disabled={isVisualizando}
                />
              </Grid>
            </Grid>
          </CardContent>
          <FormActions onVoltar={() => navigate(-1)} visualizar={isVisualizando} />
        </Box>
      </Card>
      <ModalLoading show={loading} label="Carregando dados ..." />
    </>
  );
};
export default FormUsuario;
